import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MessageCircle, User, Mail, Globe, Send, ThumbsUp } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import blogService from '../../../services/blogService';

const CommentSection = ({ postId }) => {
  const { isDark } = useTheme();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    author_name: '',
    author_email: '',
    author_website: '',
    content: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    if (!postId) return;
    fetchComments();
  }, [postId]);

  const fetchComments = async () => {
    if (!postId) return;
    try {
      setLoading(true);
      const response = await blogService.getComments(postId, { status: 'approved' });
      setComments(response?.data?.comments || []);
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.author_name || !formData.author_email || !formData.content) {
      alert('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await blogService.createComment(postId, formData);
      setSubmitSuccess(true);
      setFormData({ author_name: '', author_email: '', author_website: '', content: '' });
      fetchComments();
      setTimeout(() => setSubmitSuccess(false), 3000);
    } catch (error) {
      console.error('Error submitting comment:', error);
      alert('Failed to submit comment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const inputClass = `w-full px-3 py-2 rounded-lg text-sm border transition-colors ${
    isDark
      ? 'bg-neutral-900/60 border-neutral-800 text-neutral-200 placeholder-neutral-500 focus:border-accent-500/40'
      : 'bg-white border-neutral-200 text-neutral-800 placeholder-neutral-400 focus:border-accent-500/50'
  } focus:outline-none`;

  return (
    <div className={`rounded-xl p-5 border-gradient ${isDark ? 'glass-futuristic' : 'glass-futuristic-light'}`}>
      <h3 className={`text-lg font-semibold mb-5 flex items-center gap-2 ${isDark ? 'text-neutral-100' : 'text-neutral-900'}`}>
        <MessageCircle className="w-5 h-5 text-accent-500" />
        Comments ({comments.length})
      </h3>

      {/* Form */}
      <form onSubmit={handleSubmit} className={`mb-6 p-4 rounded-lg border ${isDark ? 'border-neutral-800' : 'border-neutral-200'}`}>
        <h4 className={`text-sm font-medium mb-3 ${isDark ? 'text-neutral-300' : 'text-neutral-700'}`}>
          Leave a comment
        </h4>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <div>
            <label className={`block text-xs mb-1 ${isDark ? 'text-neutral-400' : 'text-neutral-500'}`}>Name *</label>
            <input
              type="text"
              value={formData.author_name}
              onChange={(e) => setFormData({...formData, author_name: e.target.value})}
              className={inputClass}
              placeholder="Your name"
            />
          </div>
          <div>
            <label className={`block text-xs mb-1 ${isDark ? 'text-neutral-400' : 'text-neutral-500'}`}>Email *</label>
            <input
              type="email"
              value={formData.author_email}
              onChange={(e) => setFormData({...formData, author_email: e.target.value})}
              className={inputClass}
              placeholder="your@email.com"
            />
          </div>
        </div>

        <div className="mb-3">
          <label className={`block text-xs mb-1 ${isDark ? 'text-neutral-400' : 'text-neutral-500'}`}>Website (optional)</label>
          <input
            type="url"
            value={formData.author_website}
            onChange={(e) => setFormData({...formData, author_website: e.target.value})}
            className={inputClass}
            placeholder="https://..."
          />
        </div>

        <div className="mb-3">
          <label className={`block text-xs mb-1 ${isDark ? 'text-neutral-400' : 'text-neutral-500'}`}>Comment *</label>
          <textarea
            value={formData.content}
            onChange={(e) => setFormData({...formData, content: e.target.value})}
            rows={3}
            className={inputClass}
            placeholder="What do you think?"
          />
        </div>

        {submitSuccess && (
          <div className={`mb-3 p-2.5 rounded-lg text-xs ${isDark ? 'bg-emerald-500/10 text-emerald-400' : 'bg-emerald-50 text-emerald-600'}`}>
            Comment submitted — awaiting moderation.
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            submitting
              ? 'opacity-40 cursor-not-allowed bg-neutral-700 text-neutral-400'
              : 'bg-accent-500 text-white hover:bg-accent-600'
          }`}
        >
          <Send className="w-3.5 h-3.5" />
          {submitting ? 'Posting...' : 'Post'}
        </button>
      </form>

      {/* Comments list */}
      <div className="space-y-4">
        {loading ? (
          <p className={`text-center py-6 text-sm ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>
            Loading comments...
          </p>
        ) : comments.length === 0 ? (
          <p className={`text-center py-6 text-sm ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>
            No comments yet.
          </p>
        ) : (
          comments.map((comment) => (
            <motion.div
              key={comment.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              className={`p-4 rounded-lg ${isDark ? 'bg-neutral-900/40' : 'bg-neutral-50'}`}
            >
              <div className="flex items-start gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-neutral-800' : 'bg-neutral-200'}`}>
                  <User className={`w-4 h-4 ${isDark ? 'text-neutral-400' : 'text-neutral-500'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-sm font-medium ${isDark ? 'text-neutral-200' : 'text-neutral-800'}`}>
                      {comment.author_name}
                    </span>
                    {comment.author_website && (
                      <a href={comment.author_website} target="_blank" rel="noopener noreferrer" className="text-accent-500 hover:text-accent-400">
                        <Globe className="w-3.5 h-3.5" />
                      </a>
                    )}
                    <span className={`text-xs ml-auto ${isDark ? 'text-neutral-600' : 'text-neutral-400'}`}>
                      {formatDate(comment.created_at)}
                    </span>
                  </div>
                  <p className={`text-sm leading-relaxed ${isDark ? 'text-neutral-400' : 'text-neutral-600'}`}>
                    {comment.content}
                  </p>
                  <div className="flex items-center gap-3 mt-2">
                    <button className={`flex items-center gap-1 text-xs transition-colors ${isDark ? 'text-neutral-500 hover:text-accent-400' : 'text-neutral-400 hover:text-accent-600'}`}>
                      <ThumbsUp className="w-3 h-3" /> Like
                    </button>
                    <button className={`text-xs transition-colors ${isDark ? 'text-neutral-500 hover:text-accent-400' : 'text-neutral-400 hover:text-accent-600'}`}>
                      Reply
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
};

export default CommentSection;
